import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  fillForward,
  insertAt,
  isComplete,
  joinDigits,
  keystrokeInto,
  normalizeValue,
  replaceAt,
  sanitizeInput,
  toDigitArray,
  wholeCode,
} from "./lib";
import type { OTPInputFieldProps } from "./types";

type FocusableInput = {
  focus: () => void;
};

type UseOTPInputControllerProps = Pick<
  OTPInputFieldProps,
  "length" | "value" | "onChange" | "onComplete" | "inputType" | "disabled" | "autoFocus"
>;

export function useOTPInputController<TInput extends FocusableInput>({
  length,
  value,
  onChange,
  onComplete,
  inputType,
  disabled,
  autoFocus,
}: Required<Pick<UseOTPInputControllerProps, "length" | "inputType" | "disabled" | "autoFocus">> &
  Pick<UseOTPInputControllerProps, "value" | "onChange" | "onComplete">) {
  const inputRefs = useRef<Array<TInput | null>>([]);
  const [focusedIndex, setFocusedIndex] = useState<number>(autoFocus ? 0 : -1);
  const hasMountedRef = useRef(false);

  const normalizedValue = useMemo(
    () => normalizeValue(value, length, inputType),
    [value, length, inputType],
  );
  const digits = useMemo(() => toDigitArray(normalizedValue, length), [normalizedValue, length]);

  const focusIndex = useCallback(
    (index: number) => {
      const bounded = Math.max(0, Math.min(index, length - 1));
      inputRefs.current[bounded]?.focus();
      setFocusedIndex(bounded);
    },
    [length],
  );

  const emitChange = useCallback(
    (nextDigits: string[]) => {
      const nextValue = joinDigits(nextDigits);
      onChange?.(nextValue);
      if (isComplete(nextValue, length)) onComplete?.(nextValue);
    },
    [onChange, onComplete, length],
  );

  useEffect(() => {
    if (!autoFocus || disabled || hasMountedRef.current) return;
    hasMountedRef.current = true;
    inputRefs.current[0]?.focus();
  }, [autoFocus, disabled]);

  const handleInputValue = useCallback(
    (index: number, rawText: string, { paste = false }: { paste?: boolean } = {}) => {
      if (disabled) return;
      const sanitized = sanitizeInput(rawText, inputType);

      // Only an emptied box is a delete. A letter typed over a digit, or a
      // paste with no digits, leaves the code as it is.
      if (sanitized.length === 0) {
        if (rawText === "") emitChange(replaceAt(digits, index, ""));
        return;
      }

      // A whole code, pasted or autofilled into any box, fills every box from
      // the first (DS-030), rather than filling forward from that box. A web
      // paste carries only the clipboard text, so no box digit is in it.
      const whole = wholeCode(paste ? "" : (digits[index] ?? ""), sanitized, length);
      if (whole && "ambiguous" in whole) return;
      if (whole) {
        emitChange(toDigitArray(whole.code, length));
        focusIndex(length - 1);
        return;
      }

      // The value has no gaps, so a box past the end of the code writes at the end
      const at = Math.min(index, joinDigits(digits).length);

      if (sanitized.length === 1) {
        emitChange(replaceAt(digits, at, sanitized));
        if (at < length - 1) focusIndex(at + 1);
        return;
      }

      // A keystroke at a caret in a filled box, e.g. after Backspace shifted a
      // digit into it: insert, as the delete shifted left. A full code has no
      // room, so the digit is replaced instead. Native has no paste event, so a
      // paste that leaves the same text takes this path too.
      const typed = paste ? null : keystrokeInto(digits[index] ?? "", sanitized);
      if (typed) {
        const full = isComplete(joinDigits(digits), length);
        emitChange(full ? replaceAt(digits, index, typed) : insertAt(digits, index, typed, length));
        if (index < length - 1) focusIndex(index + 1);
        return;
      }

      const { next, nextFocusIndex } = fillForward(digits, at, sanitized, length);
      emitChange(next);
      focusIndex(nextFocusIndex);
    },
    [disabled, digits, emitChange, focusIndex, inputType, length],
  );

  const handleKeyPress = useCallback(
    (index: number, key: string) => {
      if (disabled) return;

      if (key === "Backspace") {
        // A filled box is cleared by its own edit (the change that follows
        // this key). Clearing it here as well removed two digits on web: the
        // later digits shift left, then the edit cleared the one that moved in.
        if (digits[index]) return;
        if (index > 0) {
          emitChange(replaceAt(digits, index - 1, ""));
          focusIndex(index - 1);
        }
        return;
      }

      if (key === "ArrowLeft" && index > 0) {
        focusIndex(index - 1);
      }

      if (key === "ArrowRight" && index < length - 1) {
        focusIndex(index + 1);
      }
    },
    [disabled, digits, emitChange, focusIndex, length],
  );

  const setInputRef = useCallback((index: number, node: TInput | null) => {
    inputRefs.current[index] = node;
  }, []);

  return {
    digits,
    focusedIndex,
    setFocusedIndex,
    inputRefs,
    setInputRef,
    handleInputValue,
    handleKeyPress,
  };
}
